// Loading, error and not-found states for routes. Inside the signed-in area they render
// within the shell frame, so the sidebar, the connection indicator and "Sign out" stay put.

import { brand } from '@enp/brand';
import { Button, EmptyState, ErrorState, LoadingSkeleton, Skeleton } from '@enp/ui';
import { Link, useRouter, useRouterState, type ErrorComponentProps } from '@tanstack/react-router';
import { CloudOff, LifeBuoy, MapPinOff, RotateCcw, WifiOff } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { ApiError, ContractError, NetworkError } from '../lib/api.js';
import { ShellFrame } from './app-shell.js';
import { useConnection } from './connection.js';
import { sectionForPath } from './nav.js';
import { NoAccess } from './no-access.js';
import { useOfflineCard } from './signals.js';

/** A child page is loading: skeleton inside the shell. */
export function RoutePending() {
  return (
    <>
      <h1 className="sr-only">Loading page</h1>
      <LoadingSkeleton variant="page" label="Loading page" />
    </>
  );
}

/**
 * The dashboard's shape while the session is checked: the same header, the same stat-tile
 * grid at every width (1, 2, then 4 across) and the next-step panel (UI0A-205).
 */
function DashboardSkeleton() {
  return (
    <div role="status" aria-live="polite" className="flex flex-col">
      <span className="sr-only">Loading your dashboard…</span>
      <div className="flex flex-col gap-2 pb-6">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-4 w-96 max-w-full" />
      </div>
      <Skeleton className="mb-8 h-64 w-full rounded-lg" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div
            key={i}
            className="flex flex-col gap-3 rounded-lg border border-border bg-surface-raised p-5"
          >
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** The signed-in area is loading (checking the session): the frame plus a dashboard skeleton. */
export function ShellPending() {
  return (
    <ShellFrame session="pending">
      <h1 className="sr-only">Loading your dashboard</h1>
      <DashboardSkeleton />
    </ShellFrame>
  );
}

/** A short reference for support: the server's request id, else the problem code. */
export function errorReference(error: unknown): string {
  if (error instanceof ApiError) {
    return error.requestId ?? `${error.status}-${error.code ?? 'error'}`;
  }
  if (error instanceof ContractError) return 'contract';
  return error instanceof Error ? error.name : 'unknown';
}

function supportHref(reference: string): string {
  const subject = `${brand.productName} ${brand.appNames.admin}: page didn’t load (ref ${reference})`;
  return `mailto:${brand.supportEmail}?subject=${encodeURIComponent(subject)}`;
}

export interface ErrorPanelProps {
  error: unknown;
  /** Try the failed request again. */
  onRetry: () => void;
  /** h1 when the panel IS the page; h2 when it sits inside a page with its own title. */
  titleAs?: 'h1' | 'h2';
  /** What failed to load, for the generic error title ("This page didn’t load"). */
  what?: string;
  /** An extra way out next to "Try again" (e.g. back to a list). */
  extraAction?: ReactNode;
  /**
   * The page holds something the person typed that survives the failure: the message says
   * so. Off by default: a list or report that didn't load holds nothing of theirs (review
   * UI2A-219).
   */
  inputKept?: boolean;
}

/**
 * The error panel for anything that failed to load. Offline and server-unreachable are
 * warnings that retry by themselves when the connection is back; anything else is an error
 * with a reference for support ("Error ref <request id>", B-007).
 */
export function ErrorPanel({
  error,
  onRetry,
  titleAs = 'h1',
  what = 'This page',
  extraAction,
  inputKept = false,
}: ErrorPanelProps) {
  const { online } = useConnection();
  const unreachable = error instanceof NetworkError;
  const offlineCard = unreachable && !online;
  // The shell doesn't repeat this card in its offline banner.
  useOfflineCard(offlineCard);

  // Coming back online retries by itself.
  useEffect(() => {
    if (!unreachable) return;
    window.addEventListener('online', onRetry);
    return () => window.removeEventListener('online', onRetry);
  }, [unreachable, onRetry]);

  const tryAgain = (
    <Button icon={<RotateCcw aria-hidden />} onClick={onRetry}>
      Try again
    </Button>
  );

  if (offlineCard) {
    return (
      <ErrorState
        tone="warning"
        icon={WifiOff}
        titleAs={titleAs}
        title="You’re offline"
        message={`${what} needs a connection to load. Check the Wi-Fi or network cable; it loads by itself as soon as you’re back online.`}
        actions={
          <>
            {tryAgain}
            {extraAction}
          </>
        }
      />
    );
  }
  if (unreachable) {
    return (
      <ErrorState
        tone="warning"
        icon={CloudOff}
        titleAs={titleAs}
        title={`Can’t reach ${brand.productName} right now`}
        message={`Your connection is up, but the server isn’t answering. Wait a moment and try again.${inputKept ? ' Nothing you entered was lost.' : ''}`}
        actions={
          <>
            {tryAgain}
            {extraAction}
            {/* The server's address is for whoever runs it, not the reader: tucked away (UI1A-213). */}
            {import.meta.env.DEV && (
              <details className="basis-full text-sm text-muted">
                <summary className="w-fit cursor-pointer rounded-sm">Technical details</summary>
                <p className="mt-1 font-mono text-xs">{(error as Error).message}</p>
              </details>
            )}
          </>
        }
      />
    );
  }

  const reference = errorReference(error);
  return (
    <ErrorState
      titleAs={titleAs}
      title={`${what} didn’t load`}
      message={`Something went wrong on our side.${inputKept ? ' Nothing you entered was lost.' : ''} Try again; if it keeps happening, email support and quote the reference below.`}
      detail={`Error ref ${reference}`}
      actions={
        <>
          {tryAgain}
          {extraAction}
          <Button variant="secondary" asChild>
            <a href={supportHref(reference)}>
              <LifeBuoy aria-hidden />
              Email support
            </a>
          </Button>
        </>
      }
    />
  );
}

/** A route's error: the panel, retrying by resetting the route and reloading its data. */
function RouteErrorPanel({ error, reset }: Pick<ErrorComponentProps, 'error' | 'reset'>) {
  const router = useRouter();
  const retry = () => {
    reset();
    void router.invalidate();
  };
  return <ErrorPanel error={error} onRetry={retry} />;
}

/**
 * A child page failed: the error inside the shell. A refusal for want of a permission is the
 * permission page, not an error (B-061).
 */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (error instanceof ApiError && error.status === 403) {
    return <NoAccess section={sectionForPath(pathname)?.label} />;
  }
  return (
    <div className="max-w-2xl">
      <RouteErrorPanel error={error} reset={reset} />
    </div>
  );
}

/** The session check itself failed (e.g. /me unreachable): the frame, with Sign out. */
export function ShellError({ error, reset }: ErrorComponentProps) {
  return (
    <ShellFrame session="error">
      <div className="max-w-2xl">
        <RouteErrorPanel error={error} reset={reset} />
      </div>
    </ShellFrame>
  );
}

export function NotFound() {
  return (
    <main id="main-content" className="mx-auto max-w-2xl p-6 lg:p-8">
      <EmptyState
        icon={MapPinOff}
        titleAs="h1"
        title="Page not found"
        description="That address doesn’t match any page. It may have moved, or the link may be mistyped."
        action={
          <Button asChild>
            <Link to="/">Go to dashboard</Link>
          </Button>
        }
      />
    </main>
  );
}
